import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit3, MessageCircle, Mail, Plus, UserCheck } from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";

export interface EntityHeaderProps {
  type: "ACCOUNT" | "CONTACT" | "LEAD";
  title: string;
  subtitle?: string | React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  avatarBg?: string;
  badges?: Array<{ label: string; variant?: "default" | "secondary" | "outline" | "destructive"; className?: string }>;
  owner?: { id?: string; name: string; email?: string } | null;
  createdOn?: string;
  onEdit?: () => void;
  onWhatsApp?: () => void;
  onEmail?: () => void;
  onAddTask?: () => void;
  hasWhatsApp?: boolean;
  hasEmail?: boolean;
  extraActions?: React.ReactNode;
  backUrl?: string;
}

export function EntityHeader({
  type,
  title,
  subtitle,
  icon: Icon,
  avatarBg = "bg-primary/10 text-primary border-primary/20",
  badges = [],
  owner,
  createdOn,
  onEdit,
  onWhatsApp,
  onEmail,
  onAddTask,
  hasWhatsApp,
  hasEmail,
  extraActions,
  backUrl,
}: EntityHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backUrl) navigate(backUrl);
    else navigate(-1);
  };

  const typeLabels: Record<string, string> = {
    ACCOUNT: "Account",
    CONTACT: "Contact",
    LEAD: "Lead",
  };

  return (
    <div className="bg-card border border-border/80 rounded-2xl p-5 sm:p-6 shadow-xs transition-all">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left Side: Avatar + Details */}
        <div className="flex items-start gap-4 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="size-9 rounded-xl shrink-0 -ml-1 text-muted-foreground hover:text-foreground hover:bg-muted"
            title="Go back"
          >
            <ArrowLeft className="size-4" />
          </Button>

          <div className={`size-12 sm:size-14 rounded-2xl flex items-center justify-center shrink-0 border ${avatarBg}`}>
            <Icon className="size-6 sm:size-7" />
          </div>

          <div className="space-y-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-muted text-muted-foreground font-mono">
                {typeLabels[type]}
              </span>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate">
                {title || "(Unnamed)"}
              </h1>
              {badges.map((b, i) => (
                <Badge key={i} variant={b.variant || "secondary"} className={`text-xs ${b.className || ""}`}>
                  {b.label}
                </Badge>
              ))}
            </div>

            {subtitle && (
              <div className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 truncate">
                {subtitle}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-1 text-xs text-muted-foreground">
              {owner && (
                <div className="flex items-center gap-1.5 font-medium text-foreground/80">
                  <UserCheck className="size-3.5 text-primary" />
                  <span>Owner: {owner.name}</span>
                </div>
              )}
              {createdOn && (
                <span>
                  Created {new Date(createdOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Quick Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-center shrink-0">
          {hasWhatsApp && onWhatsApp && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onWhatsApp}
              className="gap-1.5 border-[#25D366]/40 text-[#128C7E] dark:text-[#25D366] hover:bg-[#25D366]/10"
            >
              <WhatsAppIcon className="size-3.5" />
              <span>WhatsApp</span>
            </Button>
          )}

          {hasEmail && onEmail && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onEmail}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Mail className="size-3.5 text-rose-500" />
              <span>Email</span>
            </Button>
          )}

          {onAddTask && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAddTask}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Plus className="size-3.5 text-indigo-500" />
              <span>Task</span>
            </Button>
          )}

          {onEdit && (
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onEdit}
              className="gap-1.5 shadow-2xs"
            >
              <Edit3 className="size-3.5" />
              <span>Edit</span>
            </Button>
          )}

          {extraActions}
        </div>
      </div>
    </div>
  );
}
